import { useId } from "react";
import { ORDER_TOPICS, TILE_INTENTS, type OrderTopicId } from "../ids";
import type { ComposerApi } from "../useRequestComposer";
import { ChipGroup, IntentTile } from "./Choices";
import { Glyph, IntentIcon } from "./Icons";

/**
 * Passo 1 — «Di cosa hai bisogno?»: sei argomenti come tessere (radio nativi).
 * Per «Aiuto con un ordine» l'argomento dell'ordine si sceglie qui, in linea, e
 * si passa subito ai recapiti. «Preferisco scrivere» porta dritto alla nota (è
 * l'unico modo di scegliere «Altro»: niente doppioni).
 */
export function TopicStep({ api }: { api: ComposerApi }) {
  const uid = useId();
  const { t, state, steps, errors, dispatch } = api;
  const d = state.draft;
  const titleId = `${uid}-t`;
  const hintId = `${uid}-h`;
  const errId = `${uid}-e`;

  return (
    <section data-step="1" hidden={state.step !== 1} aria-labelledby={titleId} className="rq-section">
      <fieldset
        className="rq-fieldset"
        aria-describedby={[hintId, errors.intent ? errId : null].filter(Boolean).join(" ")}
        onPointerEnter={api.prefetch}
      >
        <legend className="rq-legend rq-legend--title">
          <h3 id={titleId} ref={api.setEl("h1")} tabIndex={-1} data-rq-focus-target className="rq-step__title">
            <span className="rq-sr">{t("contact.guide.a11y.stepPrefix", { n: 1, total: steps.length })} </span>
            {t("contact.guide.step1.title")}
          </h3>
        </legend>
        <p id={hintId} className="rq-step__hint">{t("contact.guide.step1.hint")}</p>
        {errors.intent && (
          <p id={errId} className="rq-err rq-err--block">
            <Glyph name="alert" size={14} />
            {t("contact.guide.step1.required", { write: t("contact.guide.step1.write") })}
          </p>
        )}
        <div className="rq-intents">
          {TILE_INTENTS.map((id, i) => (
            <IntentTile
              key={id}
              id={`${uid}-${id}`}
              name={`${uid}-intent`}
              value={id}
              checked={d.intent === id}
              label={t(`contact.guide.intents.${id}.label`)}
              desc={t(`contact.guide.intents.${id}.desc`)}
              icon={<IntentIcon id={id} />}
              onChange={() => api.chooseIntent(id)}
              inputRef={i === 0 ? api.setEl("firstIntent") : undefined}
              describedBy={errors.intent ? errId : undefined}
            />
          ))}
        </div>
      </fieldset>

      {d.intent === "order" && (
        <div className="rq-reveal" data-gb="argomento-ordine">
          <ChipGroup
            legend={t("contact.guide.q.orderTopic")}
            optional={t("contact.guide.optional")}
            options={ORDER_TOPICS.map((o) => ({ value: o, label: t(`contact.guide.orderTopic.${o}`) }))}
            value={d.orderTopic ? [d.orderTopic] : []}
            onChange={(v) => dispatch({ type: "orderTopic", id: v as OrderTopicId })}
          />
        </div>
      )}

      <div className="rq-write">
        <button type="button" className="rq-link" onClick={api.writeInstead} data-gb="scrivi">
          <Glyph name="pen" size={14} />
          {t("contact.guide.step1.write")}
        </button>
      </div>
    </section>
  );
}
